import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { useAdminContext } from '../../../contexts/AdminContext';

const ContentModals = () => {
  const {
    editingCompany,
    editingTestimonial,
    handleCreateCompany,
    handleCreateTestimonial,
    handleUpdateCompany,
    handleUpdateTestimonial,
    newCompany,
    newTestimonial,
    setEditingCompany,
    setEditingTestimonial,
    setNewCompany,
    setNewTestimonial,
    setShowCompanyModal,
    setShowTestimonialModal,
    showCompanyModal,
    showTestimonialModal,
  } = useAdminContext();

  return (
    <>
    {/* Add Testimonial Modal */}
    <Dialog open={showTestimonialModal} onOpenChange={setShowTestimonialModal}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-white">Add New Testimonial</DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-500 dark:text-slate-400">
            Add a customer testimonial to display on the homepage.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Name *</label>
            <Input
              value={newTestimonial.name}
              onChange={(e) => setNewTestimonial(prev => ({ ...prev, name: e.target.value }))}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Role / Company *</label>
            <Input
              value={newTestimonial.role}
              onChange={(e) => setNewTestimonial(prev => ({ ...prev, role: e.target.value }))}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="HR Director, Company Name"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Quote *</label>
            <textarea
              value={newTestimonial.quote}
              onChange={(e) => setNewTestimonial(prev => ({ ...prev, quote: e.target.value }))}
              className="w-full bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-md p-2 min-h-[100px]"
              placeholder="Their testimonial text..."
            />
          </div>
          <div>
            <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Photo URL (optional)</label>
            <Input
              value={newTestimonial.photo}
              onChange={(e) => setNewTestimonial(prev => ({ ...prev, photo: e.target.value }))}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="https://..."
            />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setShowTestimonialModal(false)} className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTestimonial}
              disabled={!newTestimonial.name || !newTestimonial.role || !newTestimonial.quote}
              className="bg-green-500 hover:bg-green-600"
            >
              Add Testimonial
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Edit Testimonial Modal */}
    <Dialog open={!!editingTestimonial} onOpenChange={() => setEditingTestimonial(null)}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-white">Edit Testimonial</DialogTitle>
        </DialogHeader>
        {editingTestimonial && (
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Name</label>
              <Input
                value={editingTestimonial.name}
                onChange={(e) => setEditingTestimonial(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Role / Company</label>
              <Input
                value={editingTestimonial.role}
                onChange={(e) => setEditingTestimonial(prev => ({ ...prev, role: e.target.value }))}
                className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Quote</label>
              <textarea
                value={editingTestimonial.quote}
                onChange={(e) => setEditingTestimonial(prev => ({ ...prev, quote: e.target.value }))}
                className="w-full bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-md p-2 min-h-[100px]"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Photo URL</label>
              <Input
                value={editingTestimonial.photo || ''}
                onChange={(e) => setEditingTestimonial(prev => ({ ...prev, photo: e.target.value }))}
                className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setEditingTestimonial(null)} className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleUpdateTestimonial} className="bg-blue-500 hover:bg-blue-600">
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Add Company Modal */}
    <Dialog open={showCompanyModal} onOpenChange={setShowCompanyModal}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-white">Add Trusted Company</DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-500 dark:text-slate-400">
            Add a company logo to display in the "Trusted By" section.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Company Name *</label>
            <Input
              value={newCompany.name}
              onChange={(e) => setNewCompany(prev => ({ ...prev, name: e.target.value }))}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="Company Name"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Logo URL (optional)</label>
            <Input
              value={newCompany.logo_url}
              onChange={(e) => setNewCompany(prev => ({ ...prev, logo_url: e.target.value }))}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Display Order</label>
            <Input
              type="number"
              value={newCompany.order}
              onChange={(e) => setNewCompany(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
            />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setShowCompanyModal(false)} className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCompany}
              disabled={!newCompany.name}
              className="bg-green-500 hover:bg-green-600"
            >
              Add Company
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Edit Company Modal */}
    <Dialog open={!!editingCompany} onOpenChange={() => setEditingCompany(null)}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-white">Edit Company</DialogTitle>
        </DialogHeader>
        {editingCompany && (
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Company Name</label>
              <Input
                value={editingCompany.name}
                onChange={(e) => setEditingCompany(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Logo URL</label>
              <Input
                value={editingCompany.logo_url || ''}
                onChange={(e) => setEditingCompany(prev => ({ ...prev, logo_url: e.target.value }))}
                className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400 mb-1">Display Order</label>
              <Input
                type="number"
                value={editingCompany.order || 0}
                onChange={(e) => setEditingCompany(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setEditingCompany(null)} className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleUpdateCompany} className="bg-blue-500 hover:bg-blue-600">
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ContentModals;
